import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, ArrowLeft, LogIn, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { createLoginSchema } from '@/schemas/loginSchema.ts';
import type { LoginFormValues } from '@/schemas/loginSchema.ts';
import { useAuth } from '@/context/AuthContext.tsx';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import FormError from '@/components/shared/FormError.tsx';
import { RateLimitError } from '@/api/apiService.ts';
import { formatCountdown, useRateLimitCountdown } from '@/hooks/useRateLimitCountdown.ts';

export default function LoginForm() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Destino elegido en la home MVP (p. ej. /evaluate o /pitch) antes de autenticarse.
  const next = (location.state as { next?: string } | null)?.next;
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [rateLimitSeconds, setRateLimitSeconds] = useState<number | null>(null);
  const remainingSeconds = useRateLimitCountdown(rateLimitSeconds);
  const schema = useMemo(() => createLoginSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: LoginFormValues) => {
    setSubmitError(null);
    try {
      const user = await login(data.email, data.password);
      setRateLimitSeconds(null);
      if (next) {
        navigate(next);
      } else if (user.role === 'SuperUser') {
        // SuperUser tiene su propia landing minimal en /dashboard.
        navigate('/dashboard');
      } else {
        // MVP: el usuario normal va a su flujo elegido (backend), o a la home.
        navigate(user.chosenFlow ? `/${user.chosenFlow}` : '/');
      }
    } catch (error) {
      // Robust against HMR-produced module duplication where `instanceof` can fail
      // because the class identity differs between the throwing module and ours.
      const seconds = (error as { retryAfterSeconds?: unknown })?.retryAfterSeconds;
      if (error instanceof RateLimitError || (typeof seconds === 'number' && Number.isFinite(seconds))) {
        setRateLimitSeconds(typeof seconds === 'number' ? seconds : (error as RateLimitError).retryAfterSeconds);
        setSubmitError(null);
      } else {
        setSubmitError(error instanceof Error ? error.message : t('auth.loginError'));
      }
    }
  };

  const rateLimitMessage = remainingSeconds !== null
    ? t('auth.errors.rateLimitedCountdown', {
        countdown: formatCountdown(remainingSeconds),
        defaultValue: `Demasiados intentos. Inténtalo en ${formatCountdown(remainingSeconds)}.`,
      })
    : null;
  const isLockedOut = remainingSeconds !== null;

  return (
    <div className="w-full max-w-md">
      <Link
        to="/"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors no-underline"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('auth.backLink')}
      </Link>
      <div className="rounded-2xl border border-border bg-card p-8 shadow-sm transition-shadow duration-200 hover:shadow-xl sm:p-10">
        {/* Cabecera al estilo de las tarjetas de la home (icono gradiente Agencium) */}
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-2xl text-white"
            style={{ background: 'var(--gradient-agencium)' }}
          >
            <LogIn className="size-7" />
          </div>
          <h1 className="text-xl font-bold text-foreground sm:text-2xl">
            {t('auth.loginTitle')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('auth.loginSubtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="mb-1.5 block text-sm font-medium text-foreground"
            >
              {t('auth.emailLabel')}
            </label>
            <input
              id="email"
              type="email"
              placeholder={t('auth.emailPlaceholder')}
              autoComplete="email"
              className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
              {...register('email')}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-destructive">{errors.email.message}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label
              htmlFor="password"
              className="mb-1.5 block text-sm font-medium text-foreground"
            >
              {t('auth.passwordLabel')}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.passwordPlaceholder')}
                autoComplete="current-password"
                className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                {...register('password')}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                tabIndex={-1}
                aria-label={showPassword ? t('common.hidePassword') : t('common.showPassword')}
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </Button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs text-destructive">
                {errors.password.message}
              </p>
            )}
          </div>

          <FormError
            message={rateLimitMessage ?? submitError}
            onDismiss={isLockedOut ? undefined : () => setSubmitError(null)}
          />

          {/* Submit */}
          <Button type="submit" size="lg" disabled={isSubmitting || isLockedOut} className="group w-full">
            {isSubmitting ? t('auth.loginSubmitting') : t('auth.loginSubmit')}
            <ChevronRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Button>
        </form>

        <p className="mt-4 text-center text-sm text-muted-foreground">
          <Link
            to="/forgot-password"
            className="font-medium text-primary hover:text-primary-hover transition-colors no-underline"
          >
            {t('auth.forgotPassword')}
          </Link>
        </p>

        <p className="mt-3 text-center text-sm text-muted-foreground">
          {t('auth.noAccount')}{' '}
          <Link
            to="/register"
            state={next ? { next } : undefined}
            className="font-medium text-primary hover:text-primary-hover transition-colors no-underline"
          >
            {t('auth.registerLink')}
          </Link>
        </p>
      </div>
    </div>
  );
}
